import { Component, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { ReportingOpportunitiesSearchService } from '@http/clients/reporting-app/reporting-opportunities-search.service';
import { CustomMenuItemComponent } from '@shared/custom-menu-item/custom-menu-item.component';
import { GenericTypeAheadMultiselectComponent } from 'ngx-lib';
import { FormEditorBase, createValueAccessorProvider } from '../../base/form-editor-base';

@Component({
    selector: 'app-report-opportunity-search-form-editor',
    templateUrl: './report-opportunity-search-form-editor.component.html',
    styleUrls: ['./report-opportunity-search-form-editor.component.scss'],
    providers: [createValueAccessorProvider(ReportOpportunitySearchFormEditorComponent)],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [GenericTypeAheadMultiselectComponent, CustomMenuItemComponent]
})
export class ReportOpportunitySearchFormEditorComponent extends FormEditorBase<number[] | null> {
    readonly = input<boolean>(false);
    labelText = input<string>('Opportunities');
    placeholderText = input<string>('Search Opportunities');
    hasLabel = input<boolean>(true);
    private suppressSyncEmission = false;
    private lastSyncedIdsKey = '';
    private syncRequestId = 0;

    private readonly opportunitiesSearchService = inject(ReportingOpportunitiesSearchService);

    override writeValue(value: number[] | null): void {
        this.selectedValue.set(value ?? undefined);
        void this.syncSelectedOpportunities(value);
    }

    protected override async onEntityTextChange(text: string): Promise<void> {
        if (!text?.trim()) {
            this.items.set([]);
            return;
        }
        const opportunities = await this.opportunitiesSearchService.search(text);
        this.items.set(opportunities);
    }

    protected override mapSelectedItemsToValue(): number[] | null {
        if (this.suppressSyncEmission) {
            this.suppressSyncEmission = false;
            return (this.selectedValue() as number[] | undefined) ?? null;
        }
        const selected = this.selectedItems() ?? [];
        if (!selected.length) return null;
        return selected.map((item) => Number(item.id));
    }

    protected override mapValueToSelectedItems(): any[] {
        return this.selectedItems() ?? [];
    }

    private async syncSelectedOpportunities(value: number[] | null): Promise<void> {
        const normalizedIds = (value ?? []).map((id) => Number(id)).filter((id) => Number.isFinite(id));
        const idsKey = [...normalizedIds].sort((a, b) => a - b).join(',');

        if (idsKey === this.lastSyncedIdsKey) return;
        this.lastSyncedIdsKey = idsKey;

        if (!normalizedIds.length) {
            this.suppressSyncEmission = true;
            this.selectedItems.set([]);
            return;
        }

        const requestId = ++this.syncRequestId;
        const selected = await this.opportunitiesSearchService.getByIds(normalizedIds);
        if (requestId !== this.syncRequestId) return;

        this.suppressSyncEmission = true;
        this.selectedItems.set(selected);
    }
}
